import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiPost, apiGet } from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import '../common/rtCommon.css';
import './AddInvoiceItem.css';

const AddInvoiceItem = ({ invoice, onClose, onItemAdded, isCreatingInvoice = false }) => {
  const [loading, setLoading] = useState(false);
  const [invoiceItemTypes, setInvoiceItemTypes] = useState([]);
  const now = new Date();
  const [formData, setFormData] = useState({
    invoice_item_type: '',
    description: '',
    amount: '',
    billing_month: now.getMonth() + 1,
    billing_year: now.getFullYear()
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadInvoiceItemTypes();
  }, []);

  const loadInvoiceItemTypes = async () => {
    try {
      const response = await apiGet('/invoicing/invoice-item-types');
      
      // Access response.data and ensure it's an array (same pattern as InvoiceItemTypesList)
      const data = response?.data || response || [];
      const typesList = Array.isArray(data) ? data : [];
      
      // Filter to show only active types (case-insensitive) and transform for dropdown
      const activeTypes = typesList.filter(type => 
        type.status && type.status.toLowerCase() === 'active'
      );
      
      // Transform the database results to the format expected by the dropdown
      const types = activeTypes.map(type => ({
        value: type.name,  // Use the name from database as value
        label: type.name   // Use the name from database as label (no mapping)
      }));
      setInvoiceItemTypes(types);
    } catch (error) {
      console.error('Error loading invoice item types:', error);
      toastError('Failed to load invoice item types');
      // Fallback to empty array if API fails
      setInvoiceItemTypes([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.invoice_item_type) {
      newErrors.invoice_item_type = 'Invoice item type is required';
    }

    // Description is now optional - no validation required

    if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid amount is required';
    } else {
      // Round and validate the amount to nearest whole number
      const roundedAmount = Math.round(parseFloat(formData.amount));
      if (roundedAmount <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getBedRentDescription = (month, year) => {
    const monthName = new Date(2000, month - 1).toLocaleString('en-US', { month: 'long' });
    let description = `${monthName} ${year}`;

    if (invoice && invoice.billing_period_start && invoice.billing_period_end) {
      const start = new Date(invoice.billing_period_start);
      const end = new Date(invoice.billing_period_end);
      const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
      const segDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

      if (segDays < daysInMonth) {
        const startStr = start.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        const endStr = end.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        description += ` (${startStr} to ${endStr})`;
      }
    }

    return description;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isCreatingInvoice) {
        // When creating invoice, just pass the item data to parent
        const isBedRent = formData.invoice_item_type === 'Bed Rent';
        const description = isBedRent
          ? getBedRentDescription(formData.billing_month, formData.billing_year)
          : formData.description.trim();
        const itemData = {
          invoice_item_type: formData.invoice_item_type,
          description,
          amount: Math.round(parseFloat(formData.amount)), // Round to nearest whole number
          quantity: 1,
          tax_percentage: 0.0,
          discount_amount: 0.0,
          discount_description: null
        };

        onItemAdded(itemData);
        onClose();
      } else {
        // When adding to existing invoice, create via API
        const isBedRent = formData.invoice_item_type === 'Bed Rent';
        const description = isBedRent
          ? getBedRentDescription(formData.billing_month, formData.billing_year)
          : formData.description.trim();
        const invoiceItemData = {
          invoice_id: invoice.invoice_id,
          invoice_item_type: formData.invoice_item_type,
          description,
          amount: Math.round(parseFloat(formData.amount)), // Round to nearest whole number
          quantity: 1,
          tax_percentage: 0.0,
          discount_amount: 0.0,
          discount_description: null
        };

        const response = await apiPost('/invoicing/invoice-items', invoiceItemData);

        if (response) {
          toastSuccess('Invoice item added successfully');
          onItemAdded(); // Refresh the invoice data
          onClose(); // Close the modal
        } else {
          toastError('Failed to add invoice item');
        }
      }
    } catch (error) {
      console.error('Error adding invoice item:', error);
      toastError('Failed to add invoice item');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="add-invoice-item-overlay">
      <div className="add-invoice-item-modal">
        <div className="add-invoice-item-header">
          <h2>{isCreatingInvoice ? "Add Item to Invoice" : "Add Invoice Item"}</h2>
          <button 
            className="close-button" 
            onClick={handleCancel}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Invoice Information - Only show when adding to existing invoice */}
        {!isCreatingInvoice && invoice && (
          <div className="invoice-info-section">
            <div className="invoice-info-grid">
              <div className="info-item">
                <label>Invoice Number:</label>
                <span className="invoice-number">{invoice.invoice_number}</span>
              </div>
              <div className="info-item">
                <label>Invoiced To:</label>
                <span className="paying-guest-name">
                  {invoice.paying_guest?.name || 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <label>Invoice Date:</label>
                <span>{formatDate(invoice.created_at)}</span>
              </div>
              <div className="info-item">
                <label>Current Total:</label>
                <span className="amount">₹{invoice.total_amount?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Add Item Form */}
        <form onSubmit={handleSubmit} className="add-item-form">
          <div className="form-group">
            <label htmlFor="invoice_item_type">Item Type *</label>
            <select
              id="invoice_item_type"
              name="invoice_item_type"
              value={formData.invoice_item_type}
              onChange={handleInputChange}
              className={errors.invoice_item_type ? 'error' : ''}
              disabled={loading}
            >
              <option value="">Select Item Type</option>
              {invoiceItemTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.invoice_item_type && (
              <span className="error-message">{errors.invoice_item_type}</span>
            )}
          </div>

          <div className="form-group">
            {formData.invoice_item_type === 'Bed Rent' ? (
              <>
                <label>Billing Period *</label>
                <div className="billing-period-row">
                  <select
                    name="billing_month"
                    value={formData.billing_month}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i).toLocaleString('en-US', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    name="billing_year"
                    value={formData.billing_year}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = now.getFullYear() - 1 + i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
              </>
            ) : (
              <>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter item description (optional)"
                  rows="3"
                  className={errors.description ? 'error' : ''}
                  disabled={loading}
                />
                {errors.description && (
                  <span className="error-message">{errors.description}</span>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="amount">Amount (₹) *</label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className={errors.amount ? 'error' : ''}
              disabled={loading}
            />
            {errors.amount && (
              <span className="error-message">{errors.amount}</span>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <RTButton
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
              type="button"
            >
              Cancel
            </RTButton>
            <RTButton
              variant="primary"
              type="submit"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Item'}
            </RTButton>
          </div>
        </form>

        {loading && (
          <div className="loading-overlay">
            <RTSpinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddInvoiceItem;
